/**
 * CONNOX GMBH CONFIDENTIAL
 * ________________________
 *
 * Copyright (c) 2005 - 2022 Connox GmbH, All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Connox GmbH and its suppliers, if any. The intellectual and
 * technical concepts contained herein are proprietary to Connox GmbH and
 * its suppliers and may be covered by German Laws and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * strictly forbidden unless prior written permission is obtained from
 * Connox GmbH.
 */

import {Cart} from "./Cart";
import {ICartData} from "./CartData";
import {CartRequestHandler, ICartRequestHandlerPutCartOptions} from "./requestHandlers/CartRequestHandler";

class CartFactory {
  private existingCart: Cart | null = null;
  private awaitingCartPromise: Promise<ICartData | null> | null = null;
  private awaitingCartPutPromise: Promise<ICartData> | null = null;
  /**
   * Clears the existing cart to reload it from the api - only usefull after changing the shop Id
   */
  public clearCart() {
    this.existingCart = null;
  }

  /**
   * Get the current cart or create a new one (this should be preferred over `getCart(): Cart | null`)
   * @returns {Promise<Cart | Error>}
   */
  public async generateCart(
    shopId: number,
    visitorId: string,
    config?: ICartRequestHandlerPutCartOptions,
  ): Promise<Cart> {
    if (this.existingCart && this.existingCart.visitorId === visitorId) {
      return this.existingCart;
    }

    if (this.awaitingCartPromise) {
      await this.awaitingCartPromise;
      if (this.awaitingCartPutPromise) {
        await this.awaitingCartPutPromise;
      }
      if (this.existingCart) return this.existingCart;
    }
    if (this.awaitingCartPutPromise) {
      await this.awaitingCartPutPromise;

      return this.existingCart!;
    }

    this.awaitingCartPromise = CartRequestHandler.createCart(shopId, visitorId);

    const cartResponseData = (await this.awaitingCartPromise) as ICartData;

    let rawCartData: ICartData;
    if (config) {
      this.awaitingCartPutPromise = CartRequestHandler.putParams(shopId, cartResponseData.cartId, config);
      rawCartData = await this.awaitingCartPutPromise;
    } else {
      rawCartData = cartResponseData;
    }

    this.existingCart = new Cart(shopId, rawCartData);
    this.awaitingCartPutPromise = null;
    this.awaitingCartPromise = null;

    return this.existingCart;
  }

  /**
   *  Get current cart for a visitor or returns null if no cart exists
   */
  public async getCartForVisitorId(shopId: number, visitorId: string): Promise<Cart | null> {
    if (this.existingCart && this.existingCart.visitorId === visitorId) {
      return this.existingCart;
    }

    if (this.awaitingCartPromise) {
      await this.awaitingCartPromise;

      return this.existingCart;
    }

    this.awaitingCartPromise = CartRequestHandler.getCartForVisitorId(shopId, visitorId);

    const cartResponseData = await this.awaitingCartPromise;
    this.existingCart = cartResponseData ? new Cart(shopId, cartResponseData) : null;
    this.awaitingCartPromise = null;

    return this.existingCart;
  }
}

const cartFactory = new CartFactory();
export {cartFactory as CartFactory};
